import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { searchClientDocuments, updateDocumentStatus, bulkUpdateDocumentStatus, bulkDocumentsZip } from "../../../utils/apiUtils";
import { useAuthStore } from "../../../store/authStore";

const DEFAULT_FILTERS = {
    status: "in-review",
    accounts: [],
    dateFrom: null,
    dateTo: null,
};

const DEFAULT_PAGE_SIZE = 20;

const useInvoiceReview = () => {
    const queryClient = useQueryClient();
    const { email: reviewer } = useAuthStore();

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [tokenStack, setTokenStack] = useState([]);
    const [currentToken, setCurrentToken] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const accountHccIds = appliedFilters.accounts?.length
        ? appliedFilters.accounts.map((o) => o.value)
        : undefined;

    const queryFilters = {
        status: appliedFilters.status ?? undefined,
        accountHccIds,
        dateFrom: appliedFilters.dateFrom ?? undefined,
        dateTo: appliedFilters.dateTo ?? undefined,
        limit: pageSize,
        nextToken: currentToken ?? undefined,
    };

    const queryKey = ["invoiceDocuments", queryFilters];

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        queryKey,
        queryFn: () => searchClientDocuments({
            docType: "bill",
            ...(appliedFilters.status && { status: appliedFilters.status }),
            ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
            ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
            ...(accountHccIds && { accountHccIds }),
            limit: pageSize,
            ...(currentToken && { nextToken: currentToken }),
        }),
        select: (raw) => ({
            items: raw?.documents ?? [],
            nextToken: raw?.nextToken ?? null,
        }),
        keepPreviousData: true,
    });

    const items = data?.items ?? [];
    const nextToken = data?.nextToken ?? null;

    const applyFilters = useCallback((newFilters) => {
        setAppliedFilters(newFilters);
        setTokenStack([]);
        setCurrentToken(null);
        setSelectedIds(new Set());
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        applyFilters(DEFAULT_FILTERS);
    }, [applyFilters]);

    const goNext = useCallback(() => {
        if (!nextToken) return;
        setTokenStack((prev) => [...prev, currentToken]);
        setCurrentToken(nextToken);
    }, [nextToken, currentToken]);

    const goPrevious = useCallback(() => {
        if (tokenStack.length === 0) return;
        const stack = [...tokenStack];
        const prev = stack.pop();
        setTokenStack(stack);
        setCurrentToken(prev);
    }, [tokenStack]);

    const onPageSizeChange = useCallback((newSize) => {
        setPageSize(newSize);
        setTokenStack([]);
        setCurrentToken(null);
        setSelectedIds(new Set());
    }, []);

    const toggleRowSelection = useCallback((sk) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(sk)) next.delete(sk);
            else next.add(sk);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback(() => {
        const selectableItems = items.filter((item) => item.status !== "accepted");
        if (selectedIds.size === selectableItems.length && selectableItems.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableItems.map((item) => item.sk)));
        }
    }, [selectedIds.size, items]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const canBulkAccept =
        selectedIds.size > 0 &&
        [...selectedIds].every((sk) => items.find((i) => i.sk === sk)?.status !== "accepted");

    const canBulkReject =
        selectedIds.size > 0 &&
        [...selectedIds].every((sk) => items.find((i) => i.sk === sk)?.status === "in-review");

    const actionMutation = useMutation({
        mutationFn: ({ sk, accountHccId, status }) =>
            updateDocumentStatus({ sk, accountHccId, status, reviewer }),
        onSuccess: () => {
            window.location.reload();
        },
        onError: () => {
            toast.error("Service unavailable.");
        },
    });

    const bulkActionMutation = useMutation({
        mutationFn: ({ sks, status }) => {
            const bulkItems = sks.map((sk) => {
                const item = items.find((i) => i.sk === sk);
                return { accountHccId: item?.accountHccId, sk, status };
            });
            return bulkUpdateDocumentStatus({ reviewer, items: bulkItems });
        },
        onSuccess: ({ results = [] }) => {
            const successes = results.filter((r) => r.success);
            const failures = results.filter((r) => !r.success);
            if (failures.length === 0) {
                toast.success(`${successes.length} invoice${successes.length !== 1 ? "s" : ""} updated successfully.`);
            } else if (successes.length > 0) {
                toast.success(`${successes.length} invoice${successes.length !== 1 ? "s" : ""} updated successfully.`);
                toast.warn(`${failures.length} invoice${failures.length !== 1 ? "s" : ""} could not be updated: ${failures.map((f) => f.error).join("; ")}`);
            } else {
                toast.error("No invoices could be updated.");
            }
            clearSelection();
            window.location.reload();
        },
        onError: () => {
            toast.error("Failed to update invoices. Please try again.");
        },
    });

    const bulkDownloadMutation = useMutation({
        mutationFn: async () => {
            const selectedItems = items
                .filter((item) => selectedIds.has(item.sk))
                .map((item) => ({ pk: item.pk, sk: item.sk }));
            return bulkDocumentsZip(selectedItems);
        },
        onSuccess: (data) => {
            toast.success(`Downloading ZIP with ${data.totalFiles} files`);
            window.location.href = data.downloadUrl;
        },
        onError: (err) => toast.error(err.message ?? 'Download failed'),
    });

    return {
        items,
        nextToken,
        isLoading,
        isError,
        isFetching,
        refetch,
        filters,
        setFilters,
        appliedFilters,
        applyFilters,
        clearFilters,
        pageSize,
        onPageSizeChange,
        tokenStack,
        goNext,
        goPrevious,
        selectedIds,
        toggleRowSelection,
        toggleAllSelection,
        clearSelection,
        actionMutation,
        bulkActionMutation,
        bulkDownloadMutation,
        canBulkAccept,
        canBulkReject,
    };
};

export default useInvoiceReview;
